import React, {useReducer} from "react";
import styled from "@emotion/styled";
import {useTranslation} from "react-i18next";
import {NavLink} from "react-router-dom";
import {eq, get} from "lodash";
import {Collapse} from "@mui/material";
import {INavLink} from "data/types/routing";
import {getPickEmLogo} from "data/utils";
import {GAME_ZONE_URL, SelectProjectTitle, subMenuProjectLinks} from "data/constants";
import {Language} from "data/enums";
import {PureButton} from "views/components/common/buttons/pureButton";
import {ReactComponent as Arrow} from "assets/img/icons/small-arrow.svg";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import type {IAuthController} from "views/controllers/auth/auth.controller";
import {PrimaryButton} from "views/components/common";
import {Exist} from "views/components/Exist";

const Wrapper = styled.div`
	width: 100%;
	padding: 0 20px;
	background-size: cover;
	color: ${({theme}) => theme.color.tertiary};
	border-bottom: 1px solid #a408a4;
`;

const Container = styled.div<{size: number}>`
	max-width: 1240px;
	margin: 0 auto;
	display: flex;
	gap: 12px;
	align-items: stretch;
`;

const Logo = styled.img`
	margin: 5px 24px 5px 0;
	width: 250px;
	height: auto;

	@media (max-width: 860px) {
		width: 175px;
	}

	@media (max-width: 375px) {
		width: 125px;
		margin: 15px 24px 5px 0;
	}
`;

const Link = styled(NavLink)`
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: "Hind", sans-serif;
	font-weight: 600;
	font-size: 16px;
	line-height: 140%;
	text-transform: uppercase;
	padding: 10px;
	height: 100%;
	transition: all 0.3s;
	color: #ffffff;

	&.active,
	&:hover {
		color: ${({theme}) => theme.color.primary};
		background: ${({theme}) => theme.background.primary};
	}
`;

const LinkExternal = styled.a`
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: "Hind", sans-serif;
	font-weight: 600;
	font-size: 16px;
	line-height: 140%;
	text-transform: uppercase;
	padding: 10px;
	height: 100%;
	transition: all 0.3s;
	color: #ffffff;

	&.active,
	&:hover {
		color: ${({theme}) => theme.color.primary};
		background: ${({theme}) => theme.background.primary};
	}
`;
const ProjectLinksContainer = styled.div`
	display: flex;
	flex-direction: row;
	position: relative;
	margin-left: auto;
	font-family: "Hind", sans-serif;
	font-weight: 600;
	font-size: 16px;
	line-height: 140%;
	color: ${({theme}) => theme.color.primary};

	@media (min-width: 769px) {
		background: ${({theme}) => theme.background.primary};
	}
`;

const SelectProject = styled(PureButton)`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	padding: 10px;
	font: inherit;
	gap: 10px;

	@media (min-width: 769px) {
		background: #ffffff;

		svg path {
			fill: #202020;
		}
	}
`;

const SelectProjectText = styled.span`
	color: #ffffff;
	text-align: left;

	@media (min-width: 769px) {
		display: block;
		color: ${({theme}) => theme.color.primary};
	}

	@media (max-width: 769px) {
		p {
			font-size: 12px;
			line-height: 14px;
		}
	}
`;

const ProjectLink = styled.a`
	display: flex;
	background: ${({theme}) => theme.background.primary};
	width: 100%;
	padding: 10px;
	border-top: 1px solid #cccccc;
`;

const ProjectLinks = styled.div`
	position: absolute;
	top: 100%;
	right: 0;
	z-index: 100;
	width: 100%;

	@media (min-width: 769px) {
		right: 0;
	}
`;

const CollapseLinks = styled.div`
	position: absolute;
	top: 100%;
	z-index: 100;
	opacity: 0;
	transition: all 0.2s ease-out;
	visibility: hidden;
	overflow: visible;
	box-shadow: 0 1px 2px rgba(28, 28, 30, 0.1), 0 8px 16px rgba(28, 28, 30, 0.2);
`;

const SubLink = styled(NavLink)`
	display: flex;
	align-items: center;
	font-family: "Hind", sans-serif;
	font-weight: 600;
	font-size: 16px;
	line-height: 140%;
	text-transform: uppercase;
	padding: 10px;
	color: ${({theme}) => theme.color.primary};
	background: ${({theme}) => theme.background.primary};
	min-height: 44px;
	white-space: nowrap;

	&:hover {
		background: #f6f6f6;
	}
`;

const LinkContainer = styled.div`
	position: relative;
	display: none;

	@media (min-width: 769px) {
		display: flex;
	}
	&:hover {
		.collapse-links {
			visibility: visible;
			opacity: 1;
		}

		.link-nav {
			color: ${({theme}) => theme.color.primary};
			background: ${({theme}) => theme.background.primary};
		}
	}
`;

const NotLoggedButtonsContainer = styled.div<{isFr: boolean}>`
	position: relative;
	display: flex;
	justify-content: center;

	margin: 15px 0 0 auto;

	a {
		margin: 5px;
	}

	@media (min-width: 769px) {
		display: flex;
	}

	@media (min-width: 1200px) {
		margin: 15px ${({isFr}) => (isFr ? "-110px" : "-220px")} 0 auto;
	}

	@media (max-width: 769px) {
		display: none;
	}
`;

interface ISubHeader {
	links: INavLink[];
}

export const SubHeader: React.FC<ISubHeader> = ({links}) => {
	const {t, i18n} = useTranslation();
	const logo = getPickEmLogo(i18n.language);
	const {isAuthorized} = useViewController<IAuthController>(Bindings.AuthController);
	const [isOpenProjectsLinks, toggleIsOpenProjectsLinks] = useReducer((isOpen) => !isOpen, false);
	const projectLinks = get(
		subMenuProjectLinks,
		i18n.language,
		subMenuProjectLinks[Language.English]
	) as INavLink[];

	return (
		<Wrapper className="sub-header">
			<Container size={links.length}>
				<NavLink to="/">
					<Logo src={logo} alt="CFL pickem" />
				</NavLink>
				{links.map(({title, href, urls, isPrivate, isExternal}) =>
					isPrivate && !isAuthorized ? null : (
						<LinkContainer key={title}>
							<Exist when={Boolean(isExternal)}>
								<LinkExternal
									href={href}
									className={"link-nav"}
									target={"_blank"}
									rel="noreferrer">
									{t(title)}
								</LinkExternal>
							</Exist>
							<Exist when={!isExternal}>
								<Link to={href} className={"link-nav"}>
									{t(title)}
								</Link>
							</Exist>
							{urls && (
								<CollapseLinks className={"collapse-links"}>
									{urls.map(({title, href}) => (
										<SubLink key={href} to={href}>
											{t(title)}
										</SubLink>
									))}
								</CollapseLinks>
							)}
						</LinkContainer>
					)
				)}
				<Exist when={!isAuthorized}>
					<NotLoggedButtonsContainer isFr={eq(i18n.language, "fr")}>
						<a
							href={`${GAME_ZONE_URL}login?redirectUri=${window.location.pathname}`}
							className={"link-nav"}>
							<PrimaryButton style={{background: "#00FFFF", borderColor: "#00FFFF"}}>
								{t("sub_header.login", "Login")}
							</PrimaryButton>
						</a>
						<a
							href={`${GAME_ZONE_URL}registration?redirectUri=${window.location.pathname}`}
							className={"link-nav"}>
							<PrimaryButton>
								{t("sub_header.registration", "Registration")}
							</PrimaryButton>
						</a>
					</NotLoggedButtonsContainer>
				</Exist>
				<ProjectLinksContainer>
					<SelectProject onClick={toggleIsOpenProjectsLinks}>
						<SelectProjectText>
							{t(SelectProjectTitle)}
							<p>{t("sub_header.projects.pickem_subtitle")}</p>
						</SelectProjectText>
						<Arrow />
					</SelectProject>

					<ProjectLinks>
						<Collapse in={isOpenProjectsLinks}>
							{projectLinks.map(({title, href}) => (
								<ProjectLink key={href} href={href}>
									{t(title)}
								</ProjectLink>
							))}
						</Collapse>
					</ProjectLinks>
				</ProjectLinksContainer>
			</Container>
		</Wrapper>
	);
};
